// ── Framework graph resolution ───────────────────────────────────
/**
 * How a node was reached from the pack's root framework.
 *
 * `root` is the framework the pack is built for. `overlay` is reached by
 * walking *incoming* `parent`/`applies_to` edges — an overlay that sits beneath
 * the root and therefore contributes to its pack. `cross_reference` is reached
 * by an outgoing `cross_reference` edge and is included as context at depth 1
 * only; it is never traversed through, because a cross reference asserts
 * relatedness, not containment.
 */
export const EVIDENCE_PACK_GRAPH_RELATIONS = ['root', 'overlay', 'cross_reference'];
/**
 * Named graph diagnostics.
 *
 * A diagnostic is **reported output**, never an exception and never a silent
 * truncation. AC2's requirement is that a cycle is "detected and reported as a
 * named diagnostic rather than hanging, recursing or silently truncating" —
 * these are those names.
 */
export const EVIDENCE_PACK_GRAPH_DIAGNOSTIC_CODES = [
    /** A traversal revisited a framework already on the current path. */
    'graph_cycle_detected',
    /** Traversal stopped at the depth cap; deeper nodes exist and were not visited. */
    'graph_depth_capped',
    /** Traversal stopped at the node cap; further nodes exist and were not visited. */
    'graph_node_cap_reached',
    /** An edge points at a framework code with no installed record in this org. */
    'graph_unresolved_target',
];
// ── Manifest classification ──────────────────────────────────────
/**
 * What a supplied item *is*, in regulatory terms.
 *
 * These are kept distinct because collapsing them is how a working note becomes
 * indistinguishable from a prescribed artefact in a filed pack. 62.2.2 AC2
 * asserts a draft source and an official form are not interchangeable; that
 * assertion needs this vocabulary to exist first.
 */
export const EVIDENCE_PACK_ITEM_CLASSES = [
    /** A regulator-issued form. Decided by `isOfficialForm` (the declared `is_official`), never re-derived. */
    'official_form',
    /** An internally authored template or mapping. Not prescribed by any authority. */
    'internal_template',
    /** Guidance, reference lists and explanatory content. Never evidence of compliance. */
    'guidance_reference',
    /** A completed, org-authored record — a filled form instance. */
    'completed_record',
    /**
     * Content from a reference-mode overlay. Present so an auditor can see the
     * context the organisation considered, and structurally incapable of carrying
     * an obligation (AC5).
     */
    'reference_context',
];
/** Whether the underlying source record is a live artefact or still a draft. */
export const EVIDENCE_PACK_SOURCE_STATES = ['active', 'draft'];
// ── Collector contract ───────────────────────────────────────────
/**
 * The three outcomes a collector may report. There is no fourth, and in
 * particular there is no "skipped".
 *
 * `unavailable` exists because of the 62.0.7 defect: a check that reports clean
 * over what it never opened. A collector that fails must say so in the preview
 * output, so a pack is never quietly short of a source.
 */
export const EVIDENCE_PACK_COLLECTOR_OUTCOMES = ['supplied', 'empty', 'unavailable'];
/** Why a collector could not supply a particular expected item. */
export const EVIDENCE_PACK_MISSING_REASONS = [
    /** The framework prescribes an artefact and the organisation holds no record of it. */
    'no_record',
    /** A record exists but has never been submitted or approved. */
    'draft_only',
    /** The collector itself failed; nothing from this source could be assessed. */
    'collector_unavailable',
    /** The source exists but is not usable — superseded, legacy or withdrawn. */
    'source_not_usable',
];
// ── Persisted pack records ───────────────────────────────────────
/**
 * Pack lifecycle status.
 *
 * 62.2.1 creates the collection and its read path; **62.2.2 is what writes
 * rows**. The statuses beyond `draft` are declared here so the collection's
 * schema is settled before anything persists against it, and are recorded in
 * the release notes as unreachable until their owning sprint ships rather than
 * left to look live.
 */
export const FRAMEWORK_EVIDENCE_PACK_STATUSES = [
    /** Created, not yet assembled. */
    'draft',
    /** Assembled with every expected item present (62.2.2). */
    'assembled',
    /** Assembled with a non-empty missing list (62.2.2). */
    'partial',
    /** Approved for dispatch (62.2.4). */
    'approved',
    /** Dispatched to an authority (62.2.4). */
    'submitted',
];
// ── Error codes ──────────────────────────────────────────────────
/**
 * Semantic error codes for this module (AC7).
 *
 * Every one is thrown with an `AppError` carrying `details`, so
 * `extractErrorMessage` / `extractFieldErrors` on the 62.2.5 client have real
 * content rather than a generic failure.
 *
 * That sentence used to be false. `INVALID_PREVIEW_REQUEST` was declared here
 * in 62.2.1 and **never raised anywhere** — the seven request-shape rejections
 * in `routes.ts` all answer `validation_failed` with per-field details, which is
 * what the 62.2.5 client reads. It was removed in 62.2.4 rather than wired,
 * because a fifth code that no client would branch on would have been a change
 * made to satisfy a comment.
 *
 * `error-code-reachability.test.ts` now fails, naming the code, when a member of
 * this object has no throw site. A comment cannot fail; that test can.
 */
export const FRAMEWORK_EVIDENCE_PACK_ERROR_CODES = {
    FRAMEWORK_NOT_INSTALLED: 'framework_evidence_pack_framework_not_installed',
    PACK_NOT_FOUND: 'framework_evidence_pack_not_found',
    // Sprint 62.2.2 — assembly and export.
    /** Concurrent assembly could not allocate a version after retries. */
    ASSEMBLY_VERSION_CONFLICT: 'framework_evidence_pack_assembly_version_conflict',
    /** Export requested on a pack that has never been assembled. */
    PACK_NOT_ASSEMBLED: 'framework_evidence_pack_not_assembled',
    /**
     * The source records changed since the current version was assembled, so the
     * archive would not reproduce the hash the version claims. Refusing is the
     * only honest outcome — an auditor holding a pack whose hash disagrees with
     * its recorded version has an artefact they cannot rely on.
     */
    SOURCE_CHANGED_SINCE_ASSEMBLY: 'framework_evidence_pack_source_changed_since_assembly',
    /**
     * Sprint 62.2.3 security review, finding #6. Two files resolve to one archive
     * path. `adm-zip` **overwrites** on a repeated path, so the archive would be
     * short a file while the manifest and `SHA256SUMS.txt` still listed both — a
     * pack sent to a regulator missing an evidence file, with a manifest that
     * overstates its contents.
     */
    DUPLICATE_ENTRY_PATH: 'framework_evidence_pack_duplicate_entry_path',
    /**
     * Sprint 62.2.4 security review, finding F1. Dispatch was attempted on a
     * version that has never been exported, so there is no archive to attach.
     * Sending the email without it is how a regulator receives an empty filing
     * that the platform records as successful.
     */
    NOT_EXPORTED: 'framework_evidence_pack_not_exported',
    /**
     * Sprint 62.2.4 security review, finding F3. A dispatch with this idempotency
     * key already exists for this pack — a retry or a double-clicked Send button.
     * Refused rather than emailing the regulator a second time.
     */
    DUPLICATE_DISPATCH: 'framework_evidence_pack_duplicate_dispatch',
    /**
     * Sprint 62.2.0 gate E12.5 pass 1 (F5) — a dispatch with this idempotency key
     * already exists for this pack, and the request carries DIFFERENT details
     * from the stored record (422). Nothing new is recorded; `details.field_errors`
     * names each field that differs. Distinct from DUPLICATE_DISPATCH, which is
     * answered only when the request matches the stored record, so "already
     * recorded" is never said about details that were not recorded.
     */
    DISPATCH_KEY_PAYLOAD_MISMATCH: 'framework_evidence_pack_dispatch_key_payload_mismatch',
    /**
     * Idea-catalog #47 — this organization requires a different person to approve
     * and to dispatch. Off by default, so a single-persona tenant is never locked
     * out of filing; on when an organization has the people to separate the duties.
     */
    SELF_APPROVAL_FORBIDDEN: 'framework_evidence_pack_self_approval_forbidden',
    // Sprint 62.2.4 — approval and dispatch.
    /** Dispatch attempted on a pack with no approval at all. */
    NOT_APPROVED: 'framework_evidence_pack_not_approved',
    /**
     * An approval exists but for a different version. Distinct from NOT_APPROVED
     * because the remedy differs: one means "get it approved", the other means
     * "it changed after approval, get it re-approved".
     */
    APPROVAL_VERSION_MISMATCH: 'framework_evidence_pack_approval_version_mismatch',
    /**
     * Gate E12.5 pass 1 (P3-C1) — the pack's current version or manifest hash
     * moved between the approval's source re-hash and its write (a re-assembly
     * landed in between), so nothing was approved. Read the pack again (409).
     */
    CHANGED_DURING_APPROVAL: 'framework_evidence_pack_changed_during_approval',
    /**
     * The root framework declares it has no authority that accepts filings
     * (62.0.3) — a certification body or a commercially engaged auditor. Sending
     * to one is not a stricter rule, it is a nonsense.
     */
    FRAMEWORK_ACCEPTS_NO_FILINGS: 'framework_evidence_pack_framework_accepts_no_filings',
    /** Approval attempted on a pack that has never been assembled. */
    CANNOT_APPROVE_UNASSEMBLED: 'framework_evidence_pack_cannot_approve_unassembled',
    /** A referenced user, provider config or framework does not resolve. */
    INVALID_DISPATCH_REFERENCE: 'framework_evidence_pack_invalid_dispatch_reference',
    /** The email provider rejected the send. The pack stays unsent and re-sendable. */
    DISPATCH_PROVIDER_FAILED: 'framework_evidence_pack_dispatch_provider_failed',
};
/** Collection names, exported so tests and indexes cannot drift from the service. */
export const FRAMEWORK_EVIDENCE_PACK_COLLECTION = 'grc_framework_evidence_packs';
export const FRAMEWORK_EVIDENCE_PACK_VERSION_COLLECTION = 'grc_framework_evidence_pack_versions';
// ── Sprint 62.2.2 — assembly, manifest and versioning ────────────
/**
 * What a manifest entry *is*, at the file level.
 *
 * Extends the item vocabulary with the three infrastructure entries every pack
 * carries. They are distinguished from content because a reader counting
 * "how many official forms are in this pack" must not count the manifest.
 */
export const EVIDENCE_PACK_MANIFEST_ENTRY_KINDS = [
    ...EVIDENCE_PACK_ITEM_CLASSES,
    /** The pack's own manifest. Never part of the pack hash — see below. */
    'manifest',
    /** `SHA256SUMS.txt`. */
    'sha256sums',
    /** The resolved framework graph the pack was built from. */
    'graph',
    /** The enumerated missing-item list. Present even when empty. */
    'missing_items',
];
/**
 * How a pack reached an authority.
 *
 * A closed set. `postal` and `in_person` are the two hard-copy channels and are
 * first-class rather than a comment field — a courier and a tracking number are
 * the proof a regulator asks for.
 */
export const EVIDENCE_PACK_DISPATCH_CHANNELS = [
    'email',
    'postal',
    'in_person',
    'authority_portal',
];
/**
 * `pending` is the in-flight state and it is load-bearing.
 *
 * The submission row is inserted *before* the provider is called, because the
 * unique index on `(org_id, pack_id, idempotency_key)` is what serialises
 * concurrent callers. Inserting it as `sent` means a crash or container restart
 * during the SMTP round trip leaves a permanent, append-only record asserting a
 * regulator filing that never happened — the same false-compliance-record class
 * as F1. The row is claimed as `pending` and transitions to `sent`/`failed`
 * only once the mailer has actually returned.
 */
export const EVIDENCE_PACK_DISPATCH_STATUSES = ['pending', 'sent', 'failed'];
//# sourceMappingURL=framework-evidence-pack.js.map